import { AlignerMonitoringCell, type DataTableColumn } from '@odo/shared-ui';
import type { PatientListItemType } from '../../providers/patient/PatientsListProvider';
import type { Appointment } from '../../services/appointments';
import { formatDate } from '../../utils/time';
import { ActionRequiredCell } from './cells/ActionRequiredCell';
import { ChatCell } from './cells/ChatCell';
import { LinksCell } from './cells/LinksCell';
import { NextAppointmentCell } from './cells/NextAppointmentCell';
import { PatientCell } from './cells/PatientCell';
import { PracticeCell } from './cells/PracticeCell';
import { TreatmentStatusCell } from './cells/TreatmentStatusCell';
import type { PatientsSortBy } from './patientTableSort';

const PATIENTS_TD_PADDING = 'px-6 overflow-hidden';
const PATIENTS_TH_PADDING = 'px-6';

export interface BuildPatientColumnsOptions {
    showPractice: boolean;
    onChatClick?: (patientId: string) => void;
    nextAppointmentByPatientId: Map<string, Appointment>;
    appointmentsLoading: boolean;
    /** Tags each row v1/v2; only on when the practice has a mix of both. */
    showVersionBadge: boolean;
}

export function buildPatientColumns({
    showPractice,
    onChatClick,
    nextAppointmentByPatientId,
    appointmentsLoading,
    showVersionBadge
}: BuildPatientColumnsOptions): DataTableColumn<PatientListItemType, PatientsSortBy>[] {
    return [
        {
            key: 'patient',
            label: 'PATIENT',
            width: showPractice ? '20%' : '23%',
            sortable: true,
            sortKey: 'name',
            cellClassName: 'pl-6 pr-1',
            headerClassName: 'pl-6 pr-1',
            render: (patient) => (
                <PatientCell patient={patient} showVersionBadge={showVersionBadge} />
            )
        },
        {
            key: 'chat',
            label: '',
            width: '56px',
            align: 'center',
            cellClassName: 'px-3',
            headerClassName: 'px-3',
            stopClickPropagation: true,
            render: (patient) => <ChatCell patient={patient} onChatClick={onChatClick} />
        },
        {
            key: 'practice',
            label: 'PRACTICE',
            width: '9%',
            visible: showPractice,
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => <PracticeCell patient={patient} />
        },
        {
            key: 'treatmentStatus',
            label: 'TREATMENT STATUS',
            width: showPractice ? '14%' : '15%',
            sortable: true,
            sortKey: 'treatmentStatus',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => <TreatmentStatusCell patient={patient} />
        },
        {
            key: 'startDate',
            label: 'START DATE',
            width: showPractice ? '9%' : '10%',
            align: 'center',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => (
                <span className="block truncate">{formatDate(patient.startDate ?? ' - ')}</span>
            )
        },
        {
            key: 'nextAppointment',
            label: 'NEXT APPOINTMENT',
            width: showPractice ? '13%' : '14%',
            align: 'center',
            sortable: true,
            sortKey: 'nextAppointment',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            stopClickPropagation: true,
            render: (patient) => (
                <NextAppointmentCell
                    patient={patient}
                    nextAppointment={
                        patient.id ? (nextAppointmentByPatientId.get(patient.id) ?? null) : null
                    }
                    appointmentsLoading={appointmentsLoading}
                />
            )
        },
        {
            key: 'aligners',
            label: 'ALIGNERS',
            width: '7%',
            align: 'center',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => (
                <div className="flex justify-center">
                    <AlignerMonitoringCell
                        current={patient.alignerNumber ?? null}
                        total={patient.alignerTotal ?? null}
                        monitoringStatus={patient.monitoringStatus ?? null}
                    />
                </div>
            )
        },
        {
            key: 'links',
            label: 'LINKS',
            width: showPractice ? '9%' : '10%',
            align: 'center',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            stopClickPropagation: true,
            render: (patient) => <LinksCell patient={patient} />
        },
        {
            key: 'actionRequired',
            label: 'ACTION REQUIRED',
            width: showPractice ? '15%' : '17%',
            sortable: true,
            sortKey: 'actionRequired',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            stopClickPropagation: true,
            render: (patient) => <ActionRequiredCell patient={patient} />
        }
    ];
}
